/* V155.2 — Admin Invoicing Tab.
   Lists invoices, shows revenue stats, drag-drop SLSP statement upload,
   1-click resend / SF sync / PDF download. */

import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STATUS_COLORS = {
  draft: { bg: "#1e293b", fg: "#94a3b8" },
  sent: { bg: "#1e3a8a", fg: "#93c5fd" },
  paid: { bg: "#064e3b", fg: "#6ee7b7" },
  matched_with_bank: { bg: "#365314", fg: "#bef264" },
};

function getAdminToken() {
  try {
    return (
      localStorage.getItem("admin_token") ||
      sessionStorage.getItem("admin_token") ||
      ""
    );
  } catch (_) {
    return "";
  }
}

export default function AdminInvoicingTab() {
  const [token, setToken] = useState(getAdminToken());
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [year, setYear] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stmtResult, setStmtResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const dragRef = useRef(null);

  const headers = useMemo(
    () => ({ headers: { "X-Admin-Token": token } }),
    [token]
  );

  const persistToken = (val) => {
    setToken(val);
    try {
      localStorage.setItem("admin_token", val);
    } catch (_) { /* ignore */ }
  };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (year) qs.set("year", year);
      if (statusFilter) qs.set("status", statusFilter);
      qs.set("limit", "200");
      const res = await axios.get(`${API}/invoices?${qs}`, headers);
      setRows(res.data.rows || []);
      setCount(res.data.count || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, year, statusFilter]);

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(
      (r) =>
        r.number.toLowerCase().includes(s) ||
        (r.customer?.email || "").toLowerCase().includes(s) ||
        (r.variable_symbol || "").includes(s) ||
        (r.customer?.name || "").toLowerCase().includes(s)
    );
  }, [rows, search]);

  const stats = useMemo(() => {
    const total = rows.reduce(
      (acc, r) => acc + (r.total_with_vat_eur || 0),
      0
    );
    const paid = rows
      .filter((r) => ["paid", "matched_with_bank"].includes(r.status))
      .reduce((acc, r) => acc + (r.total_with_vat_eur || 0), 0);
    const matched = rows.filter((r) => r.status === "matched_with_bank").length;
    const pending = rows.filter((r) =>
      ["draft", "sent"].includes(r.status) && !r.bank_match
    ).length;
    return { total, paid, matched, pending, count: rows.length };
  }, [rows]);

  const handleDownload = async (id, number) => {
    try {
      const res = await axios.get(`${API}/invoices/${id}/pdf`, {
        ...headers,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${number.replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert("PDF download failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleResend = async (id) => {
    if (!window.confirm("Poslať faktúru klientovi znova?")) return;
    try {
      const res = await axios.post(`${API}/invoices/${id}/resend`, {}, headers);
      alert(res.data.sent ? "✓ Email odoslaný" : `Chyba: ${res.data.reason || "neznáma"}`);
    } catch (e) {
      alert("Resend failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleSyncSf = async (id) => {
    try {
      const res = await axios.post(`${API}/invoices/${id}/sync-sf`, {}, headers);
      alert(
        res.data.uploaded
          ? `✓ Superfaktúra synced (id: ${res.data.sf_id})`
          : `SF skip/chyba: ${res.data.reason || "missing creds"}`
      );
      load();
    } catch (e) {
      alert("SF sync failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleUploadFiles = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (!/\.(xml|csv)$/i.test(file.name)) {
      alert("Súbor musí byť .xml (CAMT.053) alebo .csv");
      return;
    }
    setUploading(true);
    setStmtResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/invoices/match-statement`,
        fd,
        { headers: { ...headers.headers, "Content-Type": "multipart/form-data" } }
      );
      setStmtResult(res.data);
      load();
    } catch (e) {
      alert("Upload failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    dragRef.current?.classList.remove("ring-amber-500");
    handleUploadFiles(e.dataTransfer.files);
  };

  if (!token) {
    return (
      <div className="p-6" data-testid="invoicing-tab">
        <h2 className="text-xl font-bold mb-4">💼 Fakturácia</h2>
        <p className="text-sm text-zinc-400 mb-3">
          Vlož <strong>X-Admin-Token</strong> (z <code>/app/backend/.env</code> → <code>ADMIN_TOKEN</code>):
        </p>
        <input
          type="password"
          placeholder="ADMIN_TOKEN"
          className="bg-zinc-900 border border-zinc-700 px-3 py-2 w-full max-w-md text-sm"
          onKeyDown={(e) => {
            if (e.key === "Enter") persistToken(e.target.value);
          }}
          data-testid="invoicing-admin-token-input"
        />
        <p className="text-xs text-zinc-500 mt-2">Enter pre uloženie</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="invoicing-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-bold">💼 Fakturácia · RESTOB s.r.o.</h2>
        <button
          onClick={load}
          className="text-xs px-3 py-1.5 bg-zinc-800 border border-zinc-700 hover:border-amber-500"
          data-testid="invoicing-refresh"
        >
          🔄 Obnoviť
        </button>
      </div>

      {/* Stats tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="invoicing-stats">
        <Tile label="Faktúry spolu" value={stats.count} accent="#94a3b8" />
        <Tile label="Vystavená suma" value={`€${stats.total.toFixed(2)}`} accent="#F59E0B" />
        <Tile label="Uhradené" value={`€${stats.paid.toFixed(2)}`} accent="#10B981" />
        <Tile label="Matched z banky" value={stats.matched} accent="#84cc16" />
        <Tile label="Čakajúce" value={stats.pending} accent="#fb923c" />
      </div>

      {/* Drag-drop SLSP statement upload */}
      <div
        ref={dragRef}
        onDragOver={(e) => {
          e.preventDefault();
          dragRef.current?.classList.add("ring-amber-500");
        }}
        onDragLeave={() => dragRef.current?.classList.remove("ring-amber-500")}
        onDrop={onDrop}
        className="border-2 border-dashed border-zinc-700 ring-1 ring-transparent p-6 text-center bg-zinc-950"
        data-testid="invoicing-upload-zone"
      >
        <p className="text-sm font-bold mb-1">
          🏦 Nahraj výpis zo Slovenskej sporiteľne
        </p>
        <p className="text-xs text-zinc-400 mb-3">
          Pretiahni CAMT.053 XML alebo CSV súbor sem · alebo klikni
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xml,.csv"
          className="hidden"
          onChange={(e) => handleUploadFiles(e.target.files)}
          data-testid="invoicing-file-input"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="text-xs px-4 py-2 bg-amber-500 text-black font-bold hover:bg-amber-400"
          disabled={uploading}
          data-testid="invoicing-upload-btn"
        >
          {uploading ? "Nahrávam…" : "Vyber súbor"}
        </button>
        {stmtResult && (
          <div className="mt-4 text-left text-sm" data-testid="invoicing-stmt-result">
            <p className="text-xs text-zinc-400 mb-2">
              Formát: <code>{stmtResult.format_detected}</code> ·
              Statement: <code>{stmtResult.statement_id}</code>
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-emerald-950 border border-emerald-700 px-3 py-2">
                <div className="text-xs text-emerald-400">Matched</div>
                <div className="text-2xl font-black text-emerald-300">
                  {stmtResult.summary.matched}
                </div>
              </div>
              <div className="bg-orange-950 border border-orange-700 px-3 py-2">
                <div className="text-xs text-orange-400">Unmatched</div>
                <div className="text-2xl font-black text-orange-300">
                  {stmtResult.summary.unmatched}
                </div>
              </div>
              <div className="bg-zinc-950 border border-zinc-700 px-3 py-2">
                <div className="text-xs text-zinc-400">Skipped</div>
                <div className="text-2xl font-black text-zinc-300">
                  {stmtResult.summary.skipped}
                </div>
              </div>
            </div>
            {stmtResult.matched?.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-emerald-400 font-bold mb-1">✓ Spárované faktúry:</p>
                <ul className="text-xs text-zinc-300 space-y-1">
                  {stmtResult.matched.map((m, i) => (
                    <li key={i}>
                      <strong className="text-emerald-400">{m.invoice_number}</strong> ·
                      €{m.amount_eur} · {m.counterparty_name || "—"} · VS {m.vs}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {stmtResult.unmatched?.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-orange-400 font-bold mb-1">⚠️ Nespárované:</p>
                <ul className="text-xs text-zinc-300 space-y-1">
                  {stmtResult.unmatched.map((u, i) => (
                    <li key={i}>
                      €{u.amount_eur} · VS {u.vs || "—"} · {u.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="text"
          placeholder="Search VS / email / číslo…"
          className="bg-zinc-900 border border-zinc-700 px-3 py-1.5 text-sm flex-1 min-w-[200px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="invoicing-search"
        />
        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="bg-zinc-900 border border-zinc-700 px-3 py-1.5 text-sm"
          data-testid="invoicing-year-filter"
        >
          <option value="">Všetky roky</option>
          <option value="2026">2026</option>
          <option value="2027">2027</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-700 px-3 py-1.5 text-sm"
          data-testid="invoicing-status-filter"
        >
          <option value="">Všetky stavy</option>
          <option value="draft">draft</option>
          <option value="sent">sent</option>
          <option value="paid">paid</option>
          <option value="matched_with_bank">matched_with_bank</option>
        </select>
      </div>

      {/* Table */}
      {error && <p className="text-red-400 text-sm">{error}</p>}
      {loading ? (
        <p className="text-zinc-400 text-sm">Načítavam…</p>
      ) : (
        <div className="overflow-x-auto" data-testid="invoicing-table">
          <table className="w-full text-xs">
            <thead className="text-amber-400 border-b border-zinc-700">
              <tr>
                <Th>Číslo</Th>
                <Th>Klient</Th>
                <Th>VS</Th>
                <Th right>Suma</Th>
                <Th>Stav</Th>
                <Th>SF</Th>
                <Th>Vystavená</Th>
                <Th>Akcie</Th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-zinc-500 py-6">
                    Žiadne faktúry — Stripe payment alebo Manual create vytvorí prvú
                  </td>
                </tr>
              )}
              {filteredRows.map((inv) => (
                <tr key={inv.id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                  <td className="py-2 px-2 font-mono font-bold text-amber-300">
                    {inv.number}
                  </td>
                  <td className="py-2 px-2">
                    <div>{inv.customer?.name || inv.customer?.company || "—"}</div>
                    <div className="text-zinc-500">{inv.customer?.email}</div>
                  </td>
                  <td className="py-2 px-2 font-mono text-zinc-400">{inv.variable_symbol}</td>
                  <td className="py-2 px-2 text-right font-bold">
                    €{(inv.total_with_vat_eur || 0).toFixed(2)}
                  </td>
                  <td className="py-2 px-2">
                    <StatusBadge status={inv.status} />
                    {inv.bank_match && (
                      <div className="text-[10px] text-lime-400 mt-1">
                        🏦 {inv.bank_match.counterparty_name?.slice(0, 24) || "bank match"}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 text-zinc-400">{inv.sf_status || "—"}</td>
                  <td className="py-2 px-2 text-zinc-400">
                    {(inv.issued_at || "").slice(0, 10)}
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    <button
                      onClick={() => handleDownload(inv.id, inv.number)}
                      className="px-2 py-1 mr-1 bg-zinc-800 hover:bg-zinc-700 text-[11px]"
                      data-testid={`inv-pdf-${inv.id}`}
                      title="Stiahnuť PDF"
                    >
                      📄
                    </button>
                    <button
                      onClick={() => handleResend(inv.id)}
                      className="px-2 py-1 mr-1 bg-zinc-800 hover:bg-zinc-700 text-[11px]"
                      data-testid={`inv-resend-${inv.id}`}
                      title="Poslať klientovi"
                    >
                      📧
                    </button>
                    <button
                      onClick={() => handleSyncSf(inv.id)}
                      className="px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-[11px]"
                      data-testid={`inv-sfsync-${inv.id}`}
                      title="Sync do Superfaktúry"
                    >
                      🔄
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-zinc-500">
        {count} faktúr · TopRankPulse Agent OS · IČO 50538381
      </p>
    </div>
  );
}

const Tile = ({ label, value, accent }) => (
  <div className="bg-zinc-950 border border-zinc-800 p-3">
    <div
      className="text-[10px] uppercase tracking-widest font-black mb-1"
      style={{ color: accent }}
    >
      {label}
    </div>
    <div className="text-xl font-black" style={{ color: accent }}>
      {value}
    </div>
  </div>
);

const Th = ({ children, right }) => (
  <th
    className={`py-2 px-2 font-bold text-[10px] uppercase tracking-widest ${
      right ? "text-right" : "text-left"
    }`}
  >
    {children}
  </th>
);

const StatusBadge = ({ status }) => {
  const c = STATUS_COLORS[status] || { bg: "#1f1f1f", fg: "#aaa" };
  return (
    <span
      className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{ background: c.bg, color: c.fg }}
    >
      {status}
    </span>
  );
};
